import React from 'react';
import moment from 'moment';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import { LISTING_TYPE_BUSINESS, propTypes } from '../../util/types';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { boothTypes } from '../../config/configListing';

import businessDefaultImage from '../../assets/sitelogo.png';
import { isBookingProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.price} title={priceTitle}>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
    </div>
  );
};

const filterClasses = {
  signedUp: css.signedUp, // No shadow
  boothConfigured: css.boothConfigured, // Black shadow
  onboarded: css.onboarded, // Green shadow
  activeEngaged: css.activeEngaged, // Purple shadow
  tradeShow: css.tradeShow, // Blue shadow
};

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
  } = props;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  // Render the listing image only if listing images are enabled in the listing type
  return showListingImage ? (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <LazyImage
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  ) : (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      setActivePropsMaybe={setActivePropsMaybe}
    />
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const {
    className,
    rootClassName,
    listing,
    handleHover,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
  } = props;

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { attributes, profileImage } = currentListing.author || {};
  const { tag, rfpBids = 0, acceptedBids = 0, ratings = 0, totalRatings = 0 } = attributes.profile.publicData || {};
  const { title = '', price, createdAt, publicData } = currentListing.attributes;
  const {
    city,
    region,
    postcode,
    dbaName,
    address1,
    boothType,
    boothTypeChilds,
    companyName,
    stripeOnBoard,
    typeOfListing
  } = publicData || {};

  const getBoothFilter = boothTypes.filter((st) => boothType && boothType.length && boothType.includes(st.key));
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage = profileImage
    ? profileImage
    : currentListing.images && currentListing.images.length > 0
      ? currentListing.images[0]
      : null;


  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
      onClick: () => setActiveListing(currentListing.id),
      // onMouseEnter: () => setActiveListing(currentListing.id),
      // onMouseLeave: () => setActiveListing(null),
    }
    : null;
  const authorId = listing?.author?.id?.uuid;
  const isBusiness = typeOfListing == LISTING_TYPE_BUSINESS && authorId;

  // Determine which class to apply based on conditions
  let appliedClass = '';

  if (tag == 'Trade Show') {
    appliedClass = filterClasses.tradeShow; // Filter 5
  } else if ((rfpBids > 0) || (ratings > 0)) {
    appliedClass = filterClasses.activeEngaged; // Filter 4
  } else if (stripeOnBoard) {
    appliedClass = filterClasses.onboarded; // Filter 3
  } else if (boothType && boothType.length) {
    appliedClass = filterClasses.boothConfigured; // Filter 2
  } else {
    appliedClass = filterClasses.signedUp; // Filter 1
  }

  const classes = classNames(rootClassName || css.root, className, stripeOnBoard ? css.complete : css.incomplete, appliedClass);

  return (
    <div
      {...setActivePropsMaybe}
      onMouseOver={() => handleHover(id)}
      onMouseOut={() => handleHover(id, false)}
      className={classes}
    >
      <NamedLink
        name={isBusiness ? "ProfilePage" : "ListingPage"}
        params={{ id: isBusiness ? authorId : id, slug }}
      >
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
        >
          {firstImage ? (
            <LazyImage
              rootClassName={css.rootForImage}
              alt={title}
              image={firstImage}
              variants={variants}
              sizes={renderSizes}
            />
          ) : (
            <img
              className={css.businessDefaultImage}
              alt={"defaultLogo"}
              src={businessDefaultImage}
            />
          )}
        </AspectRatioWrapper>
      </NamedLink>
      <div className={css.info}>
        <div>
          <NamedLink
            name={isBusiness ? "ProfilePage" : "ListingPage"}
            params={{ id: isBusiness ? authorId : id, slug }}
            className={css.title}
          >
            <h3 className={css.title}>
              {companyName || title} ({dbaName})
            </h3>
          </NamedLink>
        </div>
        {city && region
          ? <p className={css.details}>
            <span><FormattedMessage id="ListingCard.city" /> </span>
            {city + ', ' + region}
          </p>
          : null}
        {getBoothFilter && getBoothFilter.length
          ? <div className={css.booths}>
            <FormattedMessage id="ListingCard.booths" />
            <ul className={css.boothsList}>
              {getBoothFilter.map((st, index) => <li key={index}>{st.key == 'other' && boothTypeChilds && boothTypeChilds.boothTypeOther ? boothTypeChilds.boothTypeOther : st.label}</li>)}
            </ul>
          </div>
          : null}
        <p className={css.details}>
          <span><FormattedMessage id="ListingCard.rating" /> </span>
          {ratings >= 1 ? ratings + ' Star' : '5 Stars'} {totalRatings ? totalRatings > 1 ? ('(' + totalRatings + ' Reviews)') : ('(' + totalRatings + ' Review)') : null}
        </p>
        <p className={css.details}>
          <span><FormattedMessage id="ListingCard.rfpsCreated" /> </span>
          {rfpBids}
        </p>
        <p className={css.details}>
          <span><FormattedMessage id="ListingCard.rfpsBidOn" /> </span>{acceptedBids}
        </p>
        <p className={css.details}>
          <span><FormattedMessage id="ListingCard.memberSince" /> </span>
          {moment(createdAt).format('MMM DD, YYYY')}
        </p>
      </div>
      {/* <ListingCardImage
        renderSizes={renderSizes}
        title={title}
        currentListing={currentListing}
        config={config}
        setActivePropsMaybe={setActivePropsMaybe}
        aspectWidth={aspectWidth}
        aspectHeight={aspectHeight}
        variantPrefix={variantPrefix}
        style={cardStyle}
        showListingImage={showListingImage}
      />
      <div className={css.info}>
        <PriceMaybe
          price={price}
          publicData={publicData}
          config={config}
          intl={intl}
          listingTypeConfig={foundListingTypeConfig}
        />
        <div className={css.mainInfo}>
          {showListingImage && (
            <div className={css.title}>
              {richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </div>
          )}
          {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </div>
          ) : null}
        </div>
      </div> */}
    </div>
  );
};

export default ListingCard;
